import {
  CertificatesContexts,
  getVesselCertificates,
  type VesselTypeCode,
  type VRCertificateType,
} from "@constants/certificates";
import type { Vessel } from "@services/vessel/types";

const TERMINAL_LOCATIONS = new Set([
  "FUJT",
  "OPS",
  "FOTT",
  "MAR",
  "VPK",
  "ADNOC",
  "DIBMAR",
  "DIBOPS",
]);

const ANCHORAGE_LOCATIONS = new Set(["FOAA", "DOAA"]);

const WARNING_PERIOD = 30; //30 days warning period

const normalizeDate = (date: Date) => {
  date.setHours(0, 0, 0, 0);
  return date;
};

const isCertificateExpired = (certificateExpiryDate?: string) => {
  if (!certificateExpiryDate) return false; // certificate without expiryDate is PERMANENT

  const expiryDate = normalizeDate(new Date(certificateExpiryDate));

  if (Number.isNaN(expiryDate.getTime())) return true;

  const now = normalizeDate(new Date());

  return expiryDate.getTime() < now.getTime();
};

const willCertificateExpireSoon = (certificateExpiryDate?: string) => {
  if (!certificateExpiryDate) return false; //certificate without expiryDate is PERMANENT

  const expiryDate = normalizeDate(new Date(certificateExpiryDate));
  if (Number.isNaN(expiryDate.getTime())) return true;

  const currentDate = normalizeDate(new Date());
  const timeDiff = expiryDate.getTime() - currentDate.getTime();
  const daysDiff = timeDiff / (1000 * 3600 * 24);
  return daysDiff <= WARNING_PERIOD;
};

export function getRequiredCertificatesForLocation(
  expectedArrivalLocation?: string,
  vesselTypeCode?: VesselTypeCode,
  grt?: number,
): VRCertificateType[] {
  if (!expectedArrivalLocation || !vesselTypeCode) return [];
  if (ANCHORAGE_LOCATIONS.has(expectedArrivalLocation)) {
    return getVesselCertificates(CertificatesContexts.ANCHORAGE, vesselTypeCode, grt)
      .filter(({ required }) => required)
      .map(({ type }) => type);
  }

  if (TERMINAL_LOCATIONS.has(expectedArrivalLocation)) {
    return getVesselCertificates(CertificatesContexts.TERMINAL, vesselTypeCode, grt)
      .filter(({ required }) => required)
      .map(({ type }) => type);
  }

  return [];
}

export function validateVesselCertificates(
  expectedArrivalLocation: string | undefined,
  vesselDetails?: Vessel | null,
  isDiplomaticVessel?: boolean,
): VRCertificateType[] {
  if (isDiplomaticVessel) {
    return [];
  }
  const vesselTypeCode = vesselDetails?.vesselType?.vesselTypeCode as VesselTypeCode | undefined;
  const requiredCertificates = getRequiredCertificatesForLocation(
    expectedArrivalLocation,
    vesselTypeCode,
    vesselDetails?.grt,
  );

  if (!requiredCertificates.length) return [];

  const vesselCertificates = vesselDetails?.vesselCertificates ?? [];

  return requiredCertificates.filter((certificateType) => {
    const certificate = vesselCertificates.find((cert) => cert.certificateType === certificateType);

    if (!certificate) return true;

    return isCertificateExpired(certificate.certificateExpiryDate);
  });
}

export function validateCertificatesWillExpireSoon(
  expectedArrivalLocation: string | undefined,
  vesselDetails?: Vessel | null,
): VRCertificateType[] {
  const vesselTypeCode = vesselDetails?.vesselType?.vesselTypeCode as VesselTypeCode | undefined;
  const requiredCertificates = getRequiredCertificatesForLocation(
    expectedArrivalLocation,
    vesselTypeCode,
    vesselDetails?.grt,
  );
  if (!requiredCertificates.length) return [];

  const vesselCertificates = vesselDetails?.vesselCertificates ?? [];
  return requiredCertificates.filter((certificateType) => {
    const certificate = vesselCertificates.find((cert) => cert.certificateType === certificateType);
    if (!certificate) return false;

    if (isCertificateExpired(certificate.certificateExpiryDate)) return false;
    return willCertificateExpireSoon(certificate.certificateExpiryDate);
  });
}
